import { useEffect, useState } from "react";
import { Autocomplete, Avatar, Box, TextField, Typography } from "@mui/material";
import { occupantsApi } from "../api/resources";
import { useLanguage } from "../i18n/LanguageContext";

const initials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

export default function OccupantPicker({ value, onChange }) {
  const { t } = useLanguage();
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (inputValue.length < 1) {
      setOptions([]);
      return;
    }
    let active = true;
    setLoading(true);
    occupantsApi
      .list(inputValue)
      .then((data) => {
        if (active) setOptions(data);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [inputValue]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInputValue(v)}
      options={options}
      loading={loading}
      getOptionLabel={(o) => o?.name || ""}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      // El servidor ya filtra por depto./nombre/teléfono — si dejamos que
      // MUI aplique también su propio filtro de texto (por default, solo
      // compara contra getOptionLabel), esconde resultados que coinciden
      // por depto. o teléfono pero no por nombre. Por eso se desactiva aquí.
      filterOptions={(x) => x}
      noOptionsText={inputValue ? t("registrar.noMatches") : t("registrar.searchHint")}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option._id} sx={{ display: "flex", gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>{initials(option.name)}</Avatar>
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.unit} · {option.role}
              {option.phone ? ` · ${option.phone}` : ""}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} label={t("registrar.pickerLabel")} placeholder={t("registrar.searchHint")} />
      )}
    />
  );
}
